import { useState, useEffect, useMemo, useRef } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid, LabelList, Legend,
  LineChart, Line,
} from 'recharts';
import SourceViewerModal from './SourceViewerModal';

const COLORS = ['#1d4ed8', '#0f766e', '#c2410c', '#0369a1', '#15803d', '#be123c', '#4338ca', '#0e7490'];
const VENDOR_COLORS = ['#6366f1', '#8b5cf6', '#d946ef', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#a855f7', '#ec4899', '#14b8a6'];

const formatCurrency = (val) => {
  if (val == null) return '--';
  if (val >= 1000000) return '$' + (val / 1000000).toFixed(1) + 'M';
  if (val >= 1000) return '$' + (val / 1000).toFixed(1) + 'k';
  return '$' + Number(val).toFixed(2);
};

const formatFullCurrency = (val) => {
  if (val == null) return '--';
  return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatMonthShort = (monthKey) => {
  if (!monthKey) return '--';
  const [year, month] = monthKey.split('-');
  return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const formatMonthLong = (monthKey) => {
  if (!monthKey) return '--';
  const [year, month] = monthKey.split('-');
  return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

function filterMonthlyData(rows, fromMonth, toMonth) {
  if (!fromMonth && !toMonth) return rows;
  return rows.filter((r) => {
    if (fromMonth && r.month < fromMonth) return false;
    if (toMonth && r.month > toMonth) return false;
    return true;
  });
}

function normalizePlaybookItem(item) {
  const priority = item?.priority === 'High' || item?.priority === 'Low' ? item.priority : 'Medium';
  const detail = item?.detail || item?.why_now || item?.rationale || 'Review this recommendation based on current spend patterns.';
  return {
    title: item?.title || 'Recommendation',
    detail,
    priority,
    expectedImpact: item?.expectedImpact || '',
    kpi: item?.kpi || '',
    evidence: item?.evidence || '',
  };
}

const SpendAnalyticsProView = ({ loaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [fromMonth, setFromMonth] = useState('');
  const [toMonth, setToMonth] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [onlyIncreases, setOnlyIncreases] = useState(false);
  const [pdfModal, setPdfModal] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [playbookState, setPlaybookState] = useState({
    loading: false,
    source: 'rules',
    warning: '',
    items: [],
  });

  const categorySectionRef = useRef(null);
  const priceSectionRef = useRef(null);
  const vendorSectionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    apiFetch('/api/invoices-v2/spend-analytics')
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.error || 'Failed to load spend analytics');
        return payload;
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const handleViewInvoicePdf = async (documentId, rawDescription) => {
    if (!documentId) return;
    const item = { description: rawDescription || '', document_id: documentId };
    setPdfModal({ invoiceId: documentId, item, url: null, loading: true });

    try {
      const res = await apiFetch(`/api/bills/${documentId}/download`);
      if (!res.ok) throw new Error('Failed to get download URL');
      const payload = await res.json();
      const enrichedItem = {
        ...item,
        invoices: { invoice_number: payload.bill_number },
        vendor_name: payload.vendor_name,
        bill_date: payload.bill_date,
      };
      setPdfModal((prev) => (prev ? { ...prev, item: enrichedItem, url: payload.downloadUrl, loading: false } : null));
    } catch {
      setPdfModal(null);
    }
  };

  const summary = data?.summary || {};
  const summaryItemCount = Number(summary?.itemCount || 0);
  const categoryBreakdown = useMemo(() => data?.categoryBreakdown || [], [data]);
  const spendOverTime = useMemo(() => data?.spendOverTime || [], [data]);
  const spendByVendorOverTime = useMemo(() => data?.spendByVendorOverTime || [], [data]);
  const topVendors = useMemo(() => data?.topVendors || [], [data]);
  const topProducts = useMemo(() => data?.topProducts || [], [data]);
  const priceTrends = useMemo(() => data?.priceTrends || [], [data]);

  const allMonths = useMemo(() => {
    return Array.from(new Set(spendOverTime.map((r) => r.month))).sort();
  }, [spendOverTime]);

  const allVendorNames = useMemo(() => {
    return topVendors.slice(0, 12).map((v) => v.name);
  }, [topVendors]);

  const filteredSpendOverTime = useMemo(() => {
    return filterMonthlyData(spendOverTime, fromMonth, toMonth);
  }, [spendOverTime, fromMonth, toMonth]);

  const filteredSpendByVendorOverTime = useMemo(() => {
    return filterMonthlyData(spendByVendorOverTime, fromMonth, toMonth);
  }, [spendByVendorOverTime, fromMonth, toMonth]);

  const filteredCategoryBreakdown = useMemo(() => {
    if (!fromMonth && !toMonth) return categoryBreakdown;

    const totals = {};
    filteredSpendOverTime.forEach((row) => {
      Object.entries(row).forEach(([k, v]) => {
        if (k === 'month') return;
        totals[k] = (totals[k] || 0) + v;
      });
    });

    const totalSpend = Object.values(totals).reduce((sum, v) => sum + v, 0);
    return categoryBreakdown
      .filter((c) => totals[c.category] != null)
      .map((c) => {
        const filteredTotal = totals[c.category] || 0;
        const scale = c.total > 0 ? filteredTotal / c.total : 0;
        return {
          ...c,
          total: Math.round(filteredTotal * 100) / 100,
          percentage: totalSpend > 0 ? Math.round((filteredTotal / totalSpend) * 10000) / 100 : 0,
          subcategories: (c.subcategories || []).map((sub) => ({
            ...sub,
            total: Math.round(sub.total * scale * 100) / 100,
          })),
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [categoryBreakdown, filteredSpendOverTime, fromMonth, toMonth]);

  const filteredTopVendors = useMemo(() => {
    let base = topVendors;

    if (fromMonth || toMonth) {
      const totals = {};
      filteredSpendByVendorOverTime.forEach((row) => {
        Object.entries(row).forEach(([k, v]) => {
          if (k === 'month') return;
          totals[k] = (totals[k] || 0) + v;
        });
      });
      base = topVendors
        .map((v) => ({ ...v, total: Math.round((totals[v.name] || 0) * 100) / 100 }))
        .filter((v) => v.total > 0);
    }

    if (vendorFilter) base = base.filter((v) => v.name === vendorFilter);
    return [...base].sort((a, b) => b.total - a.total);
  }, [topVendors, filteredSpendByVendorOverTime, fromMonth, toMonth, vendorFilter]);

  const filteredPriceTrends = useMemo(() => {
    let rows = priceTrends;
    if (vendorFilter) rows = rows.filter((row) => row.vendor === vendorFilter);
    if (onlyIncreases) rows = rows.filter((row) => (row.change_pct || 0) > 0);
    return [...rows].sort((a, b) => Math.abs(b.change_pct || 0) - Math.abs(a.change_pct || 0));
  }, [priceTrends, vendorFilter, onlyIncreases]);

  const currentTotalSpend = filteredCategoryBreakdown.reduce((sum, c) => sum + c.total, 0);
  const topCategory = filteredCategoryBreakdown[0] || null;
  const topVendor = filteredTopVendors[0] || null;
  const priceAlertCount = filteredPriceTrends.filter((row) => (row.change_pct || 0) >= 5).length;
  const isFiltered = fromMonth || toMonth || vendorFilter || onlyIncreases;
  const topVendorShare = topVendor && currentTotalSpend > 0 ? (topVendor.total / currentTotalSpend) * 100 : 0;
  const topThreeCategoryShare = currentTotalSpend > 0
    ? (filteredCategoryBreakdown.slice(0, 3).reduce((sum, c) => sum + c.total, 0) / currentTotalSpend) * 100
    : 0;

  const trendCategories = filteredCategoryBreakdown.slice(0, 6).map((c) => c.category);
  const trendData = useMemo(() => {
    return filteredSpendOverTime.map((row, index, rows) => {
      const total = trendCategories.reduce((sum, category) => sum + (Number(row[category]) || 0), 0);
      const prevTotal = index > 0
        ? trendCategories.reduce((sum, category) => sum + (Number(rows[index - 1][category]) || 0), 0)
        : null;
      const momPct = prevTotal && prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : null;
      return {
        ...row,
        __total: total,
        __momPct: momPct,
      };
    });
  }, [filteredSpendOverTime, trendCategories]);
  // Collapse long tail into "Other", separate out "Uncategorized"
  const MAX_VISIBLE_CATEGORIES = 8;
  const { displayCategories, uncategorizedEntry } = useMemo(() => {
    const uncategorized = filteredCategoryBreakdown.find((c) => c.category?.toLowerCase() === 'uncategorized');
    const categorized = filteredCategoryBreakdown.filter((c) => c.category?.toLowerCase() !== 'uncategorized');
    const visible = categorized.slice(0, MAX_VISIBLE_CATEGORIES);
    const rest = categorized.slice(MAX_VISIBLE_CATEGORIES);

    if (rest.length > 0) {
      const otherTotal = rest.reduce((sum, c) => sum + c.total, 0);
      const totalSpend = filteredCategoryBreakdown.reduce((sum, c) => sum + c.total, 0);
      visible.push({
        category: `Other (${rest.length})`,
        total: Math.round(otherTotal * 100) / 100,
        percentage: totalSpend > 0 ? Math.round((otherTotal / totalSpend) * 10000) / 100 : 0,
        subcategories: rest.map((c) => ({ name: c.category, total: c.total })),
        _isOther: true,
      });
    }

    return { displayCategories: visible, uncategorizedEntry: uncategorized || null };
  }, [filteredCategoryBreakdown]);

  const categoryColorMap = useMemo(() => {
    const map = {};
    const PRIMARY_COLOR = '#1d4ed8';
    // Map all real categories (for trend chart and detail panel)
    filteredCategoryBreakdown.forEach((c, i) => {
      if (c.category?.toLowerCase() === 'uncategorized') return;
      const opacity = 1 - (i * 0.08);
      map[c.category] = i === 0 ? PRIMARY_COLOR : `rgba(29, 78, 216, ${Math.max(0.25, opacity)})`;
    });
    // Also map the "Other" bucket for the category chart
    displayCategories.forEach((c) => {
      if (c._isOther) map[c.category] = '#d4d4d8';
    });
    return map;
  }, [filteredCategoryBreakdown, displayCategories]);

  const vendorColorMap = useMemo(() => {
    const map = {};
    filteredTopVendors.slice(0, 12).forEach((v, i) => { map[v.name] = VENDOR_COLORS[i % VENDOR_COLORS.length]; });
    return map;
  }, [filteredTopVendors]);

  const displayedCategory = filteredCategoryBreakdown.find((c) => c.category === expandedCategory) || topCategory;

  const focusTopCategory = () => {
    if (!topCategory) return;
    setExpandedCategory(topCategory.category);
    setActiveSection('categories');
    categorySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const focusPriceAlerts = () => {
    setOnlyIncreases(true);
    setActiveSection('prices');
    priceSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const focusTopVendor = () => {
    if (!topVendor) return;
    setVendorFilter(topVendor.name);
    setActiveSection('vendors');
    vendorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const resetAllFilters = () => {
    setFromMonth('');
    setToMonth('');
    setVendorFilter('');
    setOnlyIncreases(false);
    setExpandedCategory(null);
    setActiveSection(null);
  };

  const hasActiveFilters = Boolean(isFiltered || expandedCategory);
  const latestTrendRow = trendData.length > 0 ? trendData[trendData.length - 1] : null;
  const previousTrendRow = trendData.length > 1 ? trendData[trendData.length - 2] : null;
  const latestMoM = latestTrendRow?.__momPct ?? null;
  const previousMoM = previousTrendRow?.__momPct ?? null;
  const momentumShift = latestMoM != null && previousMoM != null ? latestMoM - previousMoM : null;
  const latestTrendMonth = latestTrendRow?.month || '';

  const avgAbsMoM = useMemo(() => {
    const values = trendData
      .map((row) => row.__momPct)
      .filter((value) => typeof value === 'number')
      .map((value) => Math.abs(value));

    if (values.length === 0) return null;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }, [trendData]);

  const largestIncrease = filteredPriceTrends.find((row) => (row.change_pct || 0) > 0) || null;
  const largestDecrease = filteredPriceTrends.find((row) => (row.change_pct || 0) < 0) || null;

  const activeWindowLabel = fromMonth || toMonth
    ? `${fromMonth ? formatMonthLong(fromMonth) : 'Start'} - ${toMonth ? formatMonthLong(toMonth) : 'Latest'}`
    : 'All available months';

  let storyHeadline = 'Spend is stable with clear opportunities to optimize cost.';
  if (latestMoM != null && latestMoM >= 8) {
    storyHeadline = 'Spend is accelerating; focus on price containment and vendor concentration.';
  } else if (latestMoM != null && latestMoM <= -5) {
    storyHeadline = 'Spend is decelerating; protect quality while locking in savings.';
  } else if (topThreeCategoryShare >= 70) {
    storyHeadline = 'Spend is highly concentrated in a few categories; targeted actions will move results quickly.';
  }

  const rulePlaybookItems = useMemo(() => {
    const items = [];

    if (latestMoM != null && latestMoM >= 8) {
      items.push({
        title: 'Contain current month acceleration',
        detail: `Total spend in ${formatMonthLong(latestTrendMonth)} is up ${latestMoM.toFixed(1)}% vs prior month. Validate urgent purchases and enforce approval limits.`,
        priority: 'High',
      });
    }

    if (topVendor && topVendorShare >= 25) {
      items.push({
        title: 'Reduce vendor concentration risk',
        detail: `${topVendor.name} represents ${topVendorShare.toFixed(1)}% of spend. Negotiate terms and identify a backup supplier for key SKUs.`,
        priority: topVendorShare >= 35 ? 'High' : 'Medium',
      });
    }

    if (largestIncrease && (largestIncrease.change_pct || 0) >= 5) {
      items.push({
        title: 'Investigate biggest unit-cost jump',
        detail: `${largestIncrease.name} increased ${Math.abs(largestIncrease.change_pct).toFixed(1)}% at ${largestIncrease.vendor}. Review contract terms and substitution options.`,
        priority: 'High',
      });
    }

    if (topCategory && topCategory.percentage >= 35) {
      items.push({
        title: 'Optimize highest-spend category',
        detail: `${topCategory.category} is ${topCategory.percentage.toFixed(1)}% of spend (${formatFullCurrency(topCategory.total)}). Consolidate purchasing and set target unit costs.`,
        priority: 'Medium',
      });
    }

    if (largestDecrease && (largestDecrease.change_pct || 0) <= -5) {
      items.push({
        title: 'Scale recent savings opportunity',
        detail: `${largestDecrease.name} is down ${Math.abs(largestDecrease.change_pct).toFixed(1)}%. Expand purchasing strategy to comparable items.`,
        priority: 'Low',
      });
    }

    if (items.length === 0) {
      items.push({
        title: 'Maintain current purchasing controls',
        detail: 'No major risk spikes detected in the selected range. Keep monitoring top categories and price drift weekly.',
        priority: 'Low',
      });
    }

    return items.slice(0, 4);
  }, [largestDecrease, largestIncrease, latestMoM, latestTrendMonth, topCategory, topVendor, topVendorShare]);

  useEffect(() => {
    if (!data || summaryItemCount === 0) {
      setPlaybookState((prev) => ({
        ...prev,
        loading: false,
        source: 'rules',
        warning: '',
        items: [],
      }));
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setPlaybookState((prev) => ({ ...prev, loading: true, warning: '' }));
      try {
        const res = await apiFetch('/api/invoices-v2/action-playbook-ai', {
          method: 'POST',
          body: JSON.stringify({
            filters: {
              fromMonth: fromMonth || null,
              toMonth: toMonth || null,
              vendor: vendorFilter || null,
              onlyIncreases,
            },
          }),
          signal: controller.signal,
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(payload?.error || payload?.message || 'Failed to generate AI playbook');
        }

        if (cancelled) return;
        const normalizedItems = Array.isArray(payload?.items)
          ? payload.items.map(normalizePlaybookItem).slice(0, 4)
          : [];

        setPlaybookState({
          loading: false,
          source: payload?.source || 'ai',
          warning: payload?.warning || '',
          items: normalizedItems,
        });
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        setPlaybookState({
          loading: false,
          source: 'rules',
          warning: err?.message || 'Unable to load AI recommendations right now.',
          items: [],
        });
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [data, fromMonth, onlyIncreases, summaryItemCount, toMonth, vendorFilter]);

  const finalPlaybookItems = playbookState.items.length > 0 ? playbookState.items : rulePlaybookItems;

  const renderTrendTopLabel = ({ x, y, width, index }) => {
    const row = trendData[index];
    if (!row) return null;

    const total = row.__total;
    const momPct = row.__momPct;
    const momText = momPct == null ? '--' : `${momPct >= 0 ? '+' : ''}${momPct.toFixed(1)}%`;
    const momColor = momPct == null ? '#a1a1aa' : momPct >= 0 ? '#dc2626' : '#059669';
    const labelTopY = Math.max(12, y - 16);

    return (
      <g>
        <text x={x + width / 2} y={labelTopY} textAnchor="middle" fontSize={10} fill="#27272a">
          {formatCurrency(total)}
        </text>
        <text x={x + width / 2} y={labelTopY + 11} textAnchor="middle" fontSize={10} fill={momColor}>
          {momText}
        </text>
      </g>
    );
  };

  if (loading) {
    return (
      <div data-tour="analytics-page" className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
          <p className="text-sm text-zinc-400">Analyzing spend data...</p>
        </div>
      </div>
    );
  }

  if (!data || summaryItemCount === 0) {
    return (
      <div data-tour="analytics-page" className="max-w-[1200px] mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-light text-zinc-900 mb-1">Spend Analytics</h1>
          <p className="text-sm text-zinc-400">A clean spend view appears once invoices are enriched.</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-zinc-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-sm text-zinc-600 font-medium mb-1">No enriched spend data yet</p>
          <p className="text-xs text-zinc-400">Upload invoices and process them to unlock analytics.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-tour="analytics-page" className="max-w-[1200px] mx-auto">
      {/* Header */}
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '260ms',
        }}
      >
        <h1 className="text-2xl font-light text-zinc-900 mb-1">Spend Analytics</h1>
        <p className="text-sm text-zinc-500">Decision-first view of spend, concentration, and next-best actions.</p>
      </div>

      {/* Executive Brief */}
      <div
        className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-white via-zinc-50 to-sky-50/60 mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '300ms',
        }}
      >
        <div className="absolute -top-20 -right-12 w-60 h-60 rounded-full bg-sky-100/40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-12 w-56 h-56 rounded-full bg-indigo-100/30 blur-3xl pointer-events-none" />

        <div className="relative p-5 md:p-6 grid grid-cols-1 xl:grid-cols-3 gap-5">
          <div className="xl:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-medium mb-3">Executive Brief</p>
            <h2 className="text-[clamp(1.3rem,2.3vw,1.85rem)] leading-tight font-light text-zinc-900 mb-2 max-w-3xl">
              {storyHeadline}
            </h2>
            <p className="text-sm text-zinc-600 mb-4 max-w-3xl">
              Window: {activeWindowLabel}.{' '}
              {latestTrendMonth ? (
                <>
                  Latest month ({formatMonthLong(latestTrendMonth)}) closed at{' '}
                  <span className="text-zinc-900 font-medium">{formatFullCurrency(latestTrendRow?.__total)}</span>{' '}
                  with month-over-month movement of{' '}
                  <span className={`font-medium ${latestMoM != null && latestMoM > 0 ? 'text-red-600' : latestMoM != null && latestMoM < 0 ? 'text-emerald-700' : 'text-zinc-700'}`}>
                    {latestMoM == null ? '--' : `${latestMoM >= 0 ? '+' : ''}${latestMoM.toFixed(1)}%`}
                  </span>.
                </>
              ) : 'Not enough monthly history to calculate momentum.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <StoryChip
                label="Top Category"
                value={topCategory ? `${topCategory.percentage.toFixed(0)}%` : '--'}
                tone={topCategory?.percentage >= 35 ? 'warn' : 'neutral'}
                onClick={focusTopCategory}
              />
              <StoryChip
                label="Top Vendor"
                value={topVendor ? `${topVendorShare.toFixed(0)}%` : '--'}
                tone={topVendorShare >= 30 ? 'warn' : 'neutral'}
                onClick={focusTopVendor}
              />
              <StoryChip
                label="Price Alerts"
                value={String(priceAlertCount)}
                tone={priceAlertCount >= 5 ? 'risk' : priceAlertCount > 0 ? 'warn' : 'neutral'}
                onClick={focusPriceAlerts}
              />
              <StoryChip
                label="Volatility"
                value={avgAbsMoM == null ? '--' : `${avgAbsMoM.toFixed(1)}%`}
                tone={avgAbsMoM != null && avgAbsMoM >= 7 ? 'warn' : 'neutral'}
              />
            </div>
          </div>

          <div className="bg-white/95 border border-zinc-200 rounded-xl p-4 backdrop-blur-sm">
            <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-medium mb-3">Slice The Story</p>
            <div className="space-y-2.5">
              <select
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                className="w-full text-xs border border-zinc-200 rounded-md px-2.5 py-2 text-zinc-700 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300"
              >
                <option value="">All Vendors</option>
                {allVendorNames.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={fromMonth}
                  onChange={(e) => setFromMonth(e.target.value)}
                  className="w-full text-xs border border-zinc-200 rounded-md px-2.5 py-2 text-zinc-700 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300"
                >
                  <option value="">From</option>
                  {allMonths.map((m) => <option key={m} value={m}>{formatMonthLong(m)}</option>)}
                </select>
                <select
                  value={toMonth}
                  onChange={(e) => setToMonth(e.target.value)}
                  className="w-full text-xs border border-zinc-200 rounded-md px-2.5 py-2 text-zinc-700 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300"
                >
                  <option value="">To</option>
                  {allMonths.map((m) => <option key={m} value={m}>{formatMonthLong(m)}</option>)}
                </select>
              </div>
              <button
                onClick={resetAllFilters}
                disabled={!hasActiveFilters}
                className={`w-full text-xs px-2.5 py-2 rounded-md border transition-colors ${
                  hasActiveFilters
                    ? 'border-zinc-300 text-zinc-700 bg-white hover:border-zinc-400'
                    : 'border-zinc-200 text-zinc-400 bg-zinc-50 cursor-not-allowed'
                }`}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div
        className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '340ms',
        }}
      >
        <StatCard label="Total Spend" value={formatFullCurrency(currentTotalSpend || summary.totalSpend)} highlight />
        <StatCard label="Bills" value={summary.invoiceCount?.toLocaleString?.() || summary.invoiceCount} />
        <StatCard label="Line Items" value={summary.itemCount?.toLocaleString?.() || summary.itemCount} />
        <StatCard label="Vendors" value={summary.vendorCount?.toLocaleString?.() || summary.vendorCount} />
        <StatCard label="Categories" value={summary.categoryCount?.toLocaleString?.() || summary.categoryCount} />
      </div>

      {/* Section 01: Spend Trend */}
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '360ms',
        }}
      >
        <SectionHeader
          step="01"
          title="How Spend Changed"
          description="Trend momentum and concentration context for this filtered view."
        />
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-3">
          <div className="xl:col-span-2 bg-white border border-zinc-200 rounded-xl p-4">
            {trendData.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Monthly Spend by Category</p>
                  <p className="text-xs text-zinc-500">
                    Momentum:{' '}
                    <span className={`font-medium ${momentumShift != null && momentumShift > 0 ? 'text-red-600' : momentumShift != null && momentumShift < 0 ? 'text-emerald-700' : 'text-zinc-700'}`}>
                      {momentumShift == null ? '--' : `${momentumShift >= 0 ? '+' : ''}${momentumShift.toFixed(1)} pts`}
                    </span>
                  </p>
                </div>
                <ResponsiveContainer width="100%" height={290}>
                  <BarChart data={trendData} margin={{ left: 0, right: 20, top: 42, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11, fill: '#a1a1aa' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatMonthShort}
                    />
                    <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      formatter={(value, name) => [formatFullCurrency(value), name]}
                      labelFormatter={formatMonthLong}
                      contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7', fontSize: 12 }}
                      cursor={{ fill: '#f4f4f5' }}
                    />
                    {trendCategories.map((category, i) => (
                      <Bar
                        key={category}
                        dataKey={category}
                        stackId="stack"
                        fill={categoryColorMap[category] || COLORS[i % COLORS.length]}
                        radius={i === trendCategories.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                      >
                        {i === trendCategories.length - 1 && (
                          <LabelList content={renderTrendTopLabel} />
                        )}
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </>
            ) : (
              <div className="h-[290px] flex items-center justify-center text-sm text-zinc-500">
                Not enough monthly data to visualize trend movement yet.
              </div>
            )}
          </div>

          <div className="space-y-3">
            <InsightTile
              label="Largest Category"
              title={topCategory?.category || '--'}
              detail={topCategory ? `${formatFullCurrency(topCategory.total)} -- ${topCategory.percentage.toFixed(1)}% of spend` : 'No category data.'}
              secondary={topCategory?.subcategories?.[0] ? `Top sub: ${topCategory.subcategories[0].name}` : ''}
              actionLabel="Open Category"
              onAction={focusTopCategory}
              disabled={!topCategory}
            />
            <InsightTile
              label="Vendor Concentration"
              title={topVendor?.name || '--'}
              detail={topVendor ? `${formatFullCurrency(topVendor.total)} -- ${topVendorShare.toFixed(1)}% share` : 'No vendor data.'}
              secondary={topVendor ? `${topVendor.invoiceCount} bills` : ''}
              actionLabel="Filter Vendor"
              onAction={focusTopVendor}
              disabled={!topVendor}
            />
            <InsightTile
              label="Price Pressure"
              title={`${priceAlertCount} alert${priceAlertCount !== 1 ? 's' : ''}`}
              detail={largestIncrease ? `${largestIncrease.name} up ${Math.abs(largestIncrease.change_pct || 0).toFixed(1)}%` : 'No large cost increases detected.'}
              secondary={largestIncrease ? `${largestIncrease.vendor}` : ''}
              actionLabel="Show Alerts"
              onAction={focusPriceAlerts}
              disabled={priceAlertCount === 0}
            />
          </div>
        </div>
      </div>

      {/* Section 02: Where Spend Is Concentrated */}
      <div
        ref={categorySectionRef}
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '410ms',
        }}
      >
        <SectionHeader
          step="02"
          title="Where Spend Is Concentrated"
          description="Category and vendor breakdowns to identify the biggest financial levers."
        />

        {/* Category chart + detail panel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-4">Spend by Category</h2>
            <ResponsiveContainer width="100%" height={Math.max(220, Math.min(400, displayCategories.length * 42))}>
              <BarChart data={displayCategories} layout="vertical" margin={{ left: 0, right: 80, top: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="category" width={160} tick={{ fontSize: 12, fill: '#3f3f46' }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(value, _name, props) => {
                    const pct = props?.payload?.percentage;
                    return [
                      `${formatFullCurrency(value)}${pct != null ? ` (${pct.toFixed(1)}%)` : ''}`,
                      'Spend',
                    ];
                  }}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7', fontSize: 12 }}
                />
                <Bar dataKey="total" radius={[0, 5, 5, 0]} cursor="pointer" onClick={(entry) => !entry._isOther && setExpandedCategory(entry.category)}>
                  <LabelList
                    dataKey="total"
                    position="right"
                    content={({ x, y, width, height, value, index }) => {
                      const entry = displayCategories[index];
                      const pct = entry?.percentage;
                      return (
                        <text x={x + width + 6} y={y + height / 2} dy={4} fontSize={11} fill="#52525b" fontWeight={500}>
                          {formatCurrency(value)} <tspan fill="#a1a1aa">{pct != null ? `${pct.toFixed(0)}%` : ''}</tspan>
                        </text>
                      );
                    }}
                  />
                  {displayCategories.map((entry) => (
                    <Cell
                      key={entry.category}
                      fill={categoryColorMap[entry.category] || '#d4d4d8'}
                      opacity={!expandedCategory || expandedCategory === entry.category ? 1 : 0.35}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {uncategorizedEntry && uncategorizedEntry.total > 0 && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <svg className="w-4 h-4 shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span>
                  <span className="font-medium">{formatFullCurrency(uncategorizedEntry.total)}</span> uncategorized
                  ({uncategorizedEntry.percentage?.toFixed(1) || '0'}% of spend) — review these items to improve analytics accuracy.
                </span>
              </div>
            )}
          </div>

          <CategoryDetailPanel category={displayedCategory} color={displayedCategory ? categoryColorMap[displayedCategory.category] : COLORS[0]} />
        </div>

        {/* Spend by vendor over time */}
        {filteredSpendByVendorOverTime.length > 0 && (
          <div ref={vendorSectionRef} className="bg-white border border-zinc-200 rounded-xl p-4 mt-4">
            <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-4">Spend by Vendor, Monthly</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={filteredSpendByVendorOverTime} margin={{ left: 0, right: 20, top: 20, bottom: 0 }} barSize={48}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} tickFormatter={formatMonthShort} />
                <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(value, name) => [formatFullCurrency(value), name]}
                  labelFormatter={formatMonthLong}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7', fontSize: 12 }}
                  cursor={{ fill: '#f4f4f5' }}
                />
                <Legend iconType="square" iconSize={8} wrapperStyle={{ fontSize: 11, color: '#71717a', paddingTop: 12 }} />
                {(vendorFilter ? allVendorNames.filter((v) => v === vendorFilter) : allVendorNames).map((vendor, i, arr) => (
                  <Bar key={vendor} dataKey={vendor} stackId="1" fill={vendorColorMap[vendor] || VENDOR_COLORS[i % VENDOR_COLORS.length]} radius={i === arr.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} minPointSize={2} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Vendors + Top Products side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <TopVendorsTable vendors={filteredTopVendors.slice(0, 12)} totalSpend={currentTotalSpend} />
          <TopProductsTable products={topProducts} />
        </div>
      </div>

      {/* Section 03: Price Intelligence & Action Plan */}
      <div
        ref={priceSectionRef}
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '500ms',
        }}
      >
        <SectionHeader
          step="03"
          title="Risks, Opportunities & Action Plan"
          description="Use item-level price movement and concentration to decide where to intervene first."
        />
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-3">
          <div className="xl:col-span-2 bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Price Drift</h2>
                <p className="text-[11px] text-zinc-400 mt-0.5">Largest unit-cost changes across your catalog</p>
              </div>
              <button
                onClick={() => setOnlyIncreases((v) => !v)}
                className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                  onlyIncreases
                    ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                    : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                }`}
              >
                {onlyIncreases ? 'Showing Increases' : 'Show Only Increases'}
              </button>
            </div>
            <PriceDriftTable
              trends={filteredPriceTrends.slice(0, 15)}
              onViewPdf={handleViewInvoicePdf}
              activeInvoiceId={pdfModal?.invoiceId}
            />
          </div>

          <ActionPlaybook
            items={finalPlaybookItems}
            loading={playbookState.loading}
            source={playbookState.source}
            warning={playbookState.warning}
          />
        </div>
      </div>

      {pdfModal && !pdfModal.loading && pdfModal.url && (
        <SourceViewerModal
          pdfUrl={pdfModal.url}
          item={pdfModal.item}
          allItems={[pdfModal.item]}
          onClose={() => setPdfModal(null)}
          onItemChange={() => {}}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value, highlight }) => (
  <div className={`border rounded-xl px-4 py-3 ${highlight ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
    <p className={`text-[10px] uppercase tracking-widest font-medium mb-1 ${highlight ? 'text-zinc-400' : 'text-zinc-400'}`}>{label}</p>
    <p className={`text-xl font-light ${highlight ? 'text-white' : 'text-zinc-900'}`}>{value}</p>
  </div>
);

const StoryChip = ({ label, value, tone = 'neutral', onClick }) => {
  const toneClasses = {
    neutral: 'bg-white text-zinc-700 border-zinc-200',
    warn: 'bg-amber-50 text-amber-800 border-amber-200',
    risk: 'bg-red-50 text-red-700 border-red-200',
  };

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-md border transition-all hover:shadow-sm ${toneClasses[tone] || toneClasses.neutral} ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <span className="text-zinc-400">{label}</span>
      <span className="font-medium">{value}</span>
    </button>
  );
};

const SectionHeader = ({ step, title, description }) => (
  <div className="flex items-end justify-between gap-3 flex-wrap">
    <div className="flex items-center gap-3">
      <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-medium px-2 py-1 rounded-md border border-zinc-200 bg-white">
        {step}
      </span>
      <div>
        <h2 className="text-lg font-medium text-zinc-900">{title}</h2>
        <p className="text-xs text-zinc-500">{description}</p>
      </div>
    </div>
  </div>
);

const InsightTile = ({ label, title, detail, secondary, actionLabel, onAction, disabled }) => (
  <div className="border border-zinc-200 rounded-lg p-3 bg-white hover:border-zinc-300 transition-colors">
    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1">{label}</p>
    <p className="text-sm font-medium text-zinc-900 truncate mb-1">{title}</p>
    <p className="text-xs text-zinc-600 mb-1">{detail}</p>
    {secondary ? <p className="text-[11px] text-zinc-400 mb-3 truncate">{secondary}</p> : <div className="mb-3" />}
    <button
      onClick={onAction}
      disabled={disabled}
      className="text-xs px-2.5 py-1.5 rounded-md border border-zinc-200 text-zinc-700 hover:border-zinc-300 transition-colors disabled:opacity-50"
    >
      {actionLabel}
    </button>
  </div>
);

const ActionPlaybook = ({ items, loading, source, warning }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-4">
    <div className="flex items-center justify-between gap-3 mb-2">
      <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Action Playbook</h2>
      <div className="flex items-center gap-2">
        {loading ? <span className="text-[11px] text-zinc-400">Refreshing...</span> : null}
        <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${
          source === 'ai'
            ? 'text-sky-700 border-sky-200 bg-sky-50'
            : 'text-zinc-600 border-zinc-200 bg-zinc-50'
        }`}>
          {source === 'ai' ? 'AI' : 'Rules'}
        </span>
      </div>
    </div>
    {warning ? <p className="text-[11px] text-amber-700 mb-3">{warning}</p> : <div className="mb-3" />}
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={`${item.title}-${index}`} className="border border-zinc-200 rounded-lg p-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="text-sm font-medium text-zinc-900">{item.title}</h3>
            <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border shrink-0 ${
              item.priority === 'High'
                ? 'text-red-700 border-red-200 bg-red-50'
                : item.priority === 'Medium'
                ? 'text-amber-700 border-amber-200 bg-amber-50'
                : 'text-zinc-600 border-zinc-200 bg-zinc-50'
            }`}>
              {item.priority}
            </span>
          </div>
          <p className="text-xs text-zinc-600 leading-relaxed">{item.detail}</p>
          {item.expectedImpact ? <p className="text-[11px] text-zinc-500 mt-2">Impact: {item.expectedImpact}</p> : null}
        </div>
      ))}
    </div>
  </div>
);

const CategoryDetailPanel = ({ category, color }) => {
  if (!category) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-4">
        <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-2">Category Detail</h2>
        <p className="text-sm text-zinc-500">Select a category to see where that spend is concentrated.</p>
      </div>
    );
  }

  const subcategories = (category.subcategories || []).slice(0, 7);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
        <h2 className="text-sm font-medium text-zinc-900">{category.category}</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        {formatFullCurrency(category.total)} total -- {category.percentage.toFixed(1)}% of spend
      </p>
      <div className="space-y-2.5">
        {subcategories.map((sub) => {
          const pct = category.total > 0 ? (sub.total / category.total) * 100 : 0;
          return (
            <div key={sub.name} className="flex items-center gap-3">
              <span className="text-xs text-zinc-600 w-44 shrink-0 truncate">{sub.name}</span>
              <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color, opacity: 0.75 }} />
              </div>
              <span className="text-xs text-zinc-600 w-20 text-right shrink-0">{formatFullCurrency(sub.total)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TopVendorsTable = ({ vendors, totalSpend }) => (
  <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
    <div className="px-4 py-3 border-b border-zinc-100">
      <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Top Vendors</h2>
    </div>
    <div className="overflow-y-auto max-h-[420px]">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white z-10">
          <tr className="border-b border-zinc-100">
            <th className="text-left text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Vendor</th>
            <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Invoices</th>
            <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Spend</th>
          </tr>
        </thead>
        <tbody>
          {vendors.map((v, i) => {
            const share = totalSpend > 0 ? (v.total / totalSpend) * 100 : 0;
            return (
              <tr key={v.name} className="border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-zinc-300 w-4">{i + 1}</span>
                    <span className="text-zinc-900">{v.name}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right text-zinc-500">{v.invoiceCount}</td>
                <td className="px-4 py-2.5 text-right">
                  <span className="text-zinc-900 font-medium">{formatFullCurrency(v.total)}</span>
                  <span className="text-[10px] text-zinc-400 ml-1.5">{share.toFixed(1)}%</span>
                </td>
              </tr>
            );
          })}
          {vendors.length === 0 && (
            <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-zinc-400">No vendor data in this filter range.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const TopProductsTable = ({ products }) => (
  <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
    <div className="px-4 py-3 border-b border-zinc-100">
      <h2 className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Highest Spend Products</h2>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white z-10">
          <tr className="border-b border-zinc-100">
            <th className="text-left text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Product</th>
            <th className="text-left text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Category</th>
            <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Purchases</th>
            <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Avg Unit Cost</th>
            <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Total Spend</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => (
            <tr key={p.name} className="border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors">
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-300 w-4 shrink-0">{i + 1}</span>
                  <span className="text-zinc-900 truncate max-w-[260px]">{p.name}</span>
                </div>
              </td>
              <td className="px-4 py-2.5 text-zinc-500 text-xs">{p.category}</td>
              <td className="px-4 py-2.5 text-right text-zinc-500">{p.count}</td>
              <td className="px-4 py-2.5 text-right">
                {p.avgUnitCost != null ? (
                  <span className="font-mono text-zinc-700">
                    ${p.avgUnitCost.toFixed(2)}
                    {p.unit && <span className="text-zinc-400 font-sans">/{p.unit}</span>}
                  </span>
                ) : (
                  <span className="text-zinc-400">--</span>
                )}
              </td>
              <td className="px-4 py-2.5 text-right text-zinc-900 font-medium">{formatFullCurrency(p.total)}</td>
            </tr>
          ))}
          {products.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-400">No product data available yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const MiniSparkline = ({ data, color = '#6366f1' }) => {
  if (!data || data.length < 2) return null;
  return (
    <div className="w-16 h-6">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line type="monotone" dataKey="cost" stroke={color} strokeWidth={1.5} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const PriceDriftTable = ({ trends, onViewPdf, activeInvoiceId }) => (
  <div className="overflow-y-auto max-h-[480px]">
    <style>{`@keyframes starFlash { 0%,100% { opacity: 1; } 50% { opacity: 0.2; } }`}</style>
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-white z-10">
        <tr className="border-b border-zinc-100">
          <th className="text-left text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Product</th>
          <th className="text-left text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2 hidden md:table-cell">Vendor</th>
          <th className="text-center text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2 hidden lg:table-cell">Trend</th>
          <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Latest</th>
          <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Change</th>
          <th className="text-right text-[10px] uppercase tracking-widest text-zinc-400 font-medium px-4 py-2">Action</th>
        </tr>
      </thead>
      <tbody>
        {trends.map((t) => {
          const isUp = (t.change_pct || 0) > 0;
          const isDown = (t.change_pct || 0) < 0;
          const latestEntry = [...(t.months || [])].sort((a, b) => (a.month > b.month ? 1 : -1)).pop();
          const canOpen = !!(latestEntry?.invoice_id || latestEntry?.document_id || latestEntry?.bill_id);
          const isActive = activeInvoiceId && t.months?.some((m) => (m.invoice_id || m.document_id || m.bill_id) === activeInvoiceId);

          return (
            <tr key={`${t.name}-${t.vendor}`} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors ${isActive ? 'bg-amber-50' : ''}`}>
              <td className="px-4 py-2.5">
                <div className="min-w-0 flex items-center gap-1.5">
                  {isActive && <span style={{ animation: 'starFlash 1.2s ease-in-out infinite', color: '#f59e0b', fontSize: '12px', flexShrink: 0 }}>&#x2733;</span>}
                  <div className="min-w-0">
                    <span className="block text-zinc-900 truncate max-w-[220px]">{t.name}</span>
                    <span className="block text-[10px] text-zinc-400">{t.category}{t.subcategory ? ` / ${t.subcategory}` : ''}</span>
                  </div>
                </div>
              </td>
              <td className="px-4 py-2.5 hidden md:table-cell text-zinc-500">{t.vendor}</td>
              <td className="px-4 py-2.5 hidden lg:table-cell">
                <MiniSparkline data={t.months} color={isUp ? '#ef4444' : isDown ? '#10b981' : '#6366f1'} />
              </td>
              <td className="px-4 py-2.5 text-right text-zinc-900 font-medium whitespace-nowrap">
                {formatFullCurrency(t.latest_cost)}
                {t.unit && <span className="text-zinc-400 font-normal">/{t.unit}</span>}
              </td>
              <td className="px-4 py-2.5 text-right">
                <span className={`inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${
                  isUp ? 'text-red-700 bg-red-50' : isDown ? 'text-emerald-700 bg-emerald-50' : 'text-zinc-500'
                }`}>
                  {isUp ? '\u2191' : isDown ? '\u2193' : '\u2014'} {Math.abs(t.change_pct || 0).toFixed(1)}%
                </span>
              </td>
              <td className="px-4 py-2.5 text-right">
                <button
                  onClick={() => canOpen && onViewPdf(latestEntry.invoice_id || latestEntry.document_id || latestEntry.bill_id, latestEntry.raw_description)}
                  disabled={!canOpen}
                  className="text-xs px-2.5 py-1.5 border border-zinc-200 rounded-md text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  View
                </button>
              </td>
            </tr>
          );
        })}
        {trends.length === 0 && (
          <tr>
            <td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-400">No price trend data in this filter selection.</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export default SpendAnalyticsProView;
